/**
 * Local repo intelligence (Phase 0 lite): find the repo root, build a compact
 * RepoCard from manifests, and read the project memory file. Full AST/symbol
 * indexing + retrieval is Phase 2 (`@fim/futurex-context`).
 */
import { existsSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname, join, resolve } from 'node:path';
import type { RepoCard } from '@fim/futurex-protocol';

export const MEMORY_FILENAME = 'futurex-memory.md';

/**
 * Resolve the project root. A `.git` directory wins (the usual repo root). With
 * no `.git`, prefer the OUTERMOST workspace root (pnpm/lerna/nx) so running in a
 * sub-package still indexes the whole monorepo; otherwise fall back to the
 * nearest `package.json`.
 */
const WORKSPACE_MARKERS = ['pnpm-workspace.yaml', 'lerna.json', 'nx.json', 'turbo.json'];

export function findRepoRoot(start: string = process.cwd()): string {
  let dir = resolve(start);
  let nearestPackage: string | null = null;
  let workspaceRoot: string | null = null;
  for (;;) {
    if (existsSync(join(dir, '.git'))) return dir;
    if (WORKSPACE_MARKERS.some((m) => existsSync(join(dir, m)))) workspaceRoot = dir;
    if (nearestPackage === null && existsSync(join(dir, 'package.json'))) nearestPackage = dir;
    const parent = dirname(dir);
    if (parent === dir) break; // filesystem root
    dir = parent;
  }
  return workspaceRoot ?? nearestPackage ?? resolve(start);
}

interface PackageJson {
  name?: string;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  scripts?: Record<string, string>;
  packageManager?: string;
}

function readJson(path: string): PackageJson | null {
  try {
    return JSON.parse(readFileSync(path, 'utf8')) as PackageJson;
  } catch {
    return null;
  }
}

/** Best-effort: derive a compact card from package.json + lockfiles. */
export function buildRepoCard(root: string): RepoCard {
  const pkg = readJson(join(root, 'package.json'));
  const deps: Record<string, string> = {
    ...(pkg?.dependencies ?? {}),
    ...(pkg?.devDependencies ?? {}),
  };
  const has = (name: string): boolean => name in deps;

  const card: {
    -readonly [K in keyof RepoCard]: RepoCard[K];
  } = { root };

  if (pkg?.name) card.name = pkg.name;
  if (has('typescript')) card.language = 'TypeScript';

  if (has('next')) card.framework = 'Next.js';
  else if (has('react')) card.framework = 'React';
  else if (has('vue')) card.framework = 'Vue';
  else if (has('svelte')) card.framework = 'Svelte';

  if (has('fastify')) card.backend = 'Fastify';
  else if (has('express')) card.backend = 'Express';
  else if (has('@nestjs/core')) card.backend = 'NestJS';

  if (has('prisma') || has('@prisma/client')) card.orm = 'Prisma';
  else if (has('drizzle-orm')) card.orm = 'Drizzle';
  else if (has('typeorm')) card.orm = 'TypeORM';

  if (pkg?.packageManager?.startsWith('pnpm') || existsSync(join(root, 'pnpm-lock.yaml'))) {
    card.packageManager = 'pnpm';
  } else if (existsSync(join(root, 'yarn.lock'))) card.packageManager = 'yarn';
  else if (existsSync(join(root, 'package-lock.json'))) card.packageManager = 'npm';

  const testScript = pkg?.scripts?.test ?? '';
  if (/vitest/.test(testScript)) card.test = 'vitest';
  else if (/jest/.test(testScript)) card.test = 'jest';
  else if (/playwright/.test(testScript)) card.test = 'playwright';

  return card;
}

/** Read `futurex-memory.md` from the repo root, if present. */
export function readMemory(root: string): string | null {
  const path = join(root, MEMORY_FILENAME);
  if (!existsSync(path)) return null;
  try {
    const text = readFileSync(path, 'utf8').trim();
    return text.length > 0 ? text : null;
  } catch {
    return null;
  }
}

/** Write `futurex-memory.md` at the repo root (creating or overwriting it). */
export function saveMemory(root: string, content: string): void {
  writeFileSync(join(root, MEMORY_FILENAME), content.endsWith('\n') ? content : `${content}\n`, 'utf8');
}
